import { SIMULATION_INSURANCE_TYPE } from '@/constants/simulation.const';
import rule from '@/libs/antd/rules';
import { useUserData } from '@/libs/fleet-api/hooks';
import {
	CalculateSimulationRequest,
	GetSimulationDetailResponse,
} from '@/libs/fleet-api/services/simulation';
import { mapOptionsToSelect } from '@/utils/map-options-to-select';
import {
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	InputNumber,
	Radio,
	Row,
	Select,
	Space,
} from 'antd';
import { NamePath } from 'antd/es/form/interface';

type SimInsuranceMTFClauseProps = {
	fieldName: (name: NamePath<CalculateSimulationRequest>) => typeof name;
	simulationDetailData: GetSimulationDetailResponse;
	insuranceOrder?: number;
	viewOnly?: boolean;
	tenorInYear?: number;
};

const SimInsuranceMTFClause: React.FC<SimInsuranceMTFClauseProps> = ({
	fieldName,
	simulationDetailData,
	insuranceOrder,
	viewOnly,
	tenorInYear,
}) => {
	const { groupName } = useUserData() ?? {};

	const paymentSchemeGiroOptions =
		simulationDetailData?.paymentSchemeInsuranceGiroOptions || [];

	return (
		<>
			<Row gutter={40}>
				<Col span={12}>
					<Form.Item label={`Tipe Asuransi ${insuranceOrder ?? ''}`}>
						{insuranceOrder
							? insuranceOrder === 1
								? simulationDetailData.firstInsuranceType
								: simulationDetailData.secondInsuranceType
							: simulationDetailData.insuranceType}
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('insurancePaymentId')}
						label="Pembayaran Asuransi"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('Pembayaran Asuransi wajib dipilih')]
								: []
						}
						getValueFromEvent={(value) => value}
						getValueProps={(value) => {
							// Handle both number (id) and OptionType object for initial values
							if (typeof value === 'object' && value?.id !== undefined) {
								return { value: value.id };
							}
							return { value };
						}}
					>
						<Select
							options={mapOptionsToSelect(
								simulationDetailData?.insurancePaymentOptions,
							)}
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />

			<Row gutter={40}>
				<Col span={12}>
					<Form.Item
						name={fieldName('insuranceRegion')}
						label="Wilayah Asuransi"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('Wilayah Asuransi wajib dipilih')]
								: []
						}
						getValueFromEvent={(value) => value}
						getValueProps={(value) => {
							// Handle both number (id) and OptionType object for initial values
							if (typeof value === 'object' && value?.id !== undefined) {
								return { value: value.id };
							}
							return { value };
						}}
					>
						<Select
							options={mapOptionsToSelect(
								simulationDetailData?.insuranceRegionOptions,
							)}
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('insuranceRateLimitId')}
						label="Batas Premi"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('Batas Premi wajib dipilih')]
								: []
						}
						getValueFromEvent={(value) => value}
						getValueProps={(value) => {
							// Handle both number (id) and OptionType object for initial values
							if (typeof value === 'object' && value?.id !== undefined) {
								return { value: value.id };
							}
							return { value };
						}}
					>
						<Select
							options={mapOptionsToSelect(
								simulationDetailData?.insuranceRateLimitOptions,
							)}
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={40}>
				{simulationDetailData.insuranceType ===
					SIMULATION_INSURANCE_TYPE.COMBINATION ||
				simulationDetailData.insuranceType ===
					SIMULATION_INSURANCE_TYPE.REKANAN ? (
					<Col
						span={24}
						style={{ display: 'flex', gap: 4 }}
					>
						<Form.Item
							label={`Asuransi Tenor ${insuranceOrder}`}
							name={fieldName('mtfClauseStartDate')}
							rules={[{ required: true }]}
						>
							<DatePicker
								style={{ borderRadius: 8 }}
								format="DD-MM-YYYY"
							/>
						</Form.Item>
						<Form.Item name={fieldName('mtfClauseEndDate')}>
							<DatePicker
								style={{ borderRadius: 8 }}
								format="DD-MM-YYYY"
							/>
						</Form.Item>
					</Col>
				) : (
					<>
						<Col span={12}>
							<Form.Item
								name={fieldName('insuranceTenorTypeId')}
								label={`Asuransi Tenor ${insuranceOrder ?? ''}`}
								rules={
									!(groupName === 'FCOH' || groupName === 'FRM')
										? [rule.required('Asuransi Tenor wajib dipilih')]
										: []
								}
								labelCol={{ span: 12 }}
								getValueFromEvent={(value) => value}
								getValueProps={(value) => {
									// Handle both number (id) and OptionType object for initial values
									if (typeof value === 'object' && value?.id !== undefined) {
										return { value: value.id };
									}
									return { value };
								}}
							>
								<Select
									options={mapOptionsToSelect(
										simulationDetailData?.insuranceTenorTypeOptions,
									)}
									style={{ width: '100%' }}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName('insuranceDiscountPercentage')}
								label="Discount Asuransi"
								rules={[
									{
										type: 'number',
										min: 0,
										max:
											simulationDetailData?.maximumInsuranceDiscountPercentage ??
											100,
										message:
											'*Discount Asuransi yang Anda inputkan berada di bawah ketentuan yang berlaku. Silakan perbarui Diskon Asuransi sesuai ketentuan.',
									},
								]}
							>
								<InputNumber
									style={{ width: '100%' }}
									placeholder="Input Diskon (%)"
									disabled={viewOnly}
									suffix="%"
								/>
							</Form.Item>
						</Col>
					</>
				)}
				<Divider />
			</Row>
			<Row gutter={40}>
				{/* For Radio.Group, labelCol/wrapperCol might not be as effective without wrapping in a new Form.Item */}
				<Col span={12}>
					<Form.Item
						name={fieldName('isFlood')}
						label="Banjir"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('Banjir wajib dipilih')]
								: []
						}
					>
						<Radio.Group>
							<Space direction="horizontal">
								<Radio value={true}>Ya</Radio>
								<Radio value={false}>Tidak</Radio>
							</Space>
						</Radio.Group>
					</Form.Item>
				</Col>

				<Col span={12}>
					<Form.Item
						name={fieldName('isEarthquake')}
						label="Gempa"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('Gempa wajib dipilih')]
								: []
						}
					>
						<Radio.Group>
							<Radio value={true}>Ya</Radio>
							<Radio value={false}>Tidak</Radio>
						</Radio.Group>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={40}>
				<Col span={12}>
					<Form.Item
						name={fieldName('isRiot')}
						label="Huru-Hara"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('Huru-Hara wajib dipilih')]
								: []
						}
					>
						<Radio.Group>
							<Radio value={true}>Ya</Radio>
							<Radio value={false}>Tidak</Radio>
						</Radio.Group>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('isTerrorism')}
						label="Teroris"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('Teroris wajib dipilih')]
								: []
						}
					>
						<Radio.Group>
							<Radio value={true}>Ya</Radio>
							<Radio value={false}>Tidak</Radio>
						</Radio.Group>
					</Form.Item>
				</Col>
			</Row>

			<Divider />
			<Row gutter={40}>
				<Col span={12}>
					<Form.Item
						name={fieldName('tjhAmount')}
						label="TJH"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('TJH wajib diisi')]
								: []
						}
						getValueFromEvent={(value) => value}
						getValueProps={(value) => {
							// Handle both number/string (id) and OptionType object for initial values
							if (typeof value === 'object' && value?.id !== undefined) {
								return { value: value.id };
							}
							// For decimal fields, treat 0 as undefined (no selection)
							// if (value === 0 || value === '0') {
							// 	return { value: undefined };
							// }
							return { value };
						}}
					>
						<Select
							options={mapOptionsToSelect(
								simulationDetailData?.tjhAmountOptions,
							)}
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('driverPersonalAccident')}
						label="PA Pengemudi"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('PA Pengemudi wajib dipilih')]
								: []
						}
						getValueFromEvent={(value) => value}
						getValueProps={(value) => {
							// Handle both number/string (id) and OptionType object for initial values
							if (typeof value === 'object' && value?.id !== undefined) {
								return { value: value.id };
							}
							// For decimal fields, treat 0 as undefined (no selection)
							// if (value === 0 || value === '0') {
							// 	return { value: undefined };
							// }
							return { value };
						}}
					>
						<Select
							options={mapOptionsToSelect(
								simulationDetailData?.driverPersonalAccidentOptions,
							)}
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={40}>
				<Col span={12}>
					<Form.Item
						name={fieldName('passengerPersonalAccident')}
						label="PA Penumpang"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('PA Penumpang wajib diisi')]
								: []
						}
						getValueFromEvent={(value) => value}
						getValueProps={(value) => {
							// Handle both number/string (id) and OptionType object for initial values
							if (typeof value === 'object' && value?.id !== undefined) {
								return { value: value.id };
							}
							// For decimal fields, treat 0 as undefined (no selection)
							// if (value === 0 || value === '0') {
							// 	return { value: undefined };
							// }
							return { value };
						}}
					>
						<Select
							options={mapOptionsToSelect(
								simulationDetailData?.passengerPersonalAccidentOptions,
							)}
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('totalPassenger')}
						label="Jumlah Penumpang"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('Jumlah Penumpang wajib dipilih')]
								: []
						}
						getValueFromEvent={(value) => value}
						getValueProps={(value) => {
							// Handle both number (id) and OptionType object for initial values
							if (typeof value === 'object' && value?.id !== undefined) {
								return { value: value.id };
							}
							return { value };
						}}
					>
						<Select
							options={mapOptionsToSelect(
								simulationDetailData?.passengerCountOptions,
							)}
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</Col>
			</Row>

			<Divider />
			<Row
				gutter={16}
				style={{ marginBottom: 16 }}
			>
				{/* For Radio.Group, labelCol/wrapperCol might not be as effective without wrapping in a new Form.Item */}

				<Col span={12}>
					<Form.Item
						name={fieldName('isHic')}
						label="HIC"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('HIC wajib dipilih')]
								: []
						}
					>
						<Radio.Group>
							<Radio value={true}>Ya</Radio>
							<Radio value={false}>Tidak</Radio>
						</Radio.Group>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('isGap')}
						label="GAP"
						rules={
							!(groupName === 'FCOH' || groupName === 'FRM')
								? [rule.required('GAP wajib dipilih')]
								: []
						}
					>
						<Radio.Group>
							<Radio value={true}>Ya</Radio>
							<Radio value={false}>Tidak</Radio>
						</Radio.Group>
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			{/* New Payment Fields */}
			<Form.Item
				noStyle
				shouldUpdate={() => true}
			>
				{({ getFieldValue }) => {
					// 1. Analyze Main Insurance Payment (Top Dropdown)
					const mainPaymentId = getFieldValue(fieldName('insurancePaymentId'));
					const mainPaymentOptions =
						simulationDetailData?.insurancePaymentOptions || [];
					const selectedMainPayment = mainPaymentOptions.find(
						(opt) => opt.id === mainPaymentId,
					);
					const mainLabel = selectedMainPayment?.value || '';

					// Condition 1: Only show if value is "Full Cash"
					if (mainLabel !== 'Full Cash') {
						return null;
					}

					// Parse Years Covered (e.g. "1 Tahun", "2 Thn")
					const yearsMatch = mainLabel.match(/(\d+)\s*(?:Tahun|Thn|Year)/i);
					const yearsCovered = yearsMatch ? parseInt(yearsMatch[1], 10) : 0;

					const maxTenor = tenorInYear || 1;

					// 2. Analyze Lower Payment (Full Cash Type) for Scheme Visibility
					const paymentId = getFieldValue(
						fieldName('insurancePaymentFullCashTypeId'),
					);
					const paymentOptions =
						simulationDetailData?.paymentInsuranceFullCashOptions || [];
					const selectedPayment = paymentOptions.find(
						(opt) => opt.id === paymentId,
					);

					// Condition: "Skema Pembayaran" only appears if "Pilih Pembayaran" value is "PDC/Giro"
					const isPdcGiro =
						selectedPayment?.value?.toLowerCase().includes('pdc') ||
						selectedPayment?.value?.toLowerCase().includes('giro');

					return (
						<>
							<Row gutter={40}>
								<Col span={12}>
									<Form.Item
										name={fieldName('insurancePaymentFullCashTypeId')}
										label="Pilih Pembayaran"
										rules={[
											{
												required: true,
												message: 'Pilih Pembayaran wajib dipilih',
											},
										]}
									>
										<Select
											options={mapOptionsToSelect(
												simulationDetailData?.paymentInsuranceFullCashOptions,
											)}
											style={{ width: '100%' }}
											placeholder="Pilih Pembayaran"
											allowClear
											disabled={viewOnly}
										/>
									</Form.Item>
								</Col>

								{/* Condition 2: Show Scheme ONLY if Lower Payment is PDC/Giro */}
								{isPdcGiro && (
									<Col span={12}>
										<Form.Item
											name={fieldName('insurancePaymentSchemeGiroTypeId')}
											label="Skema Pembayaran"
											rules={[
												{
													required: true,
													message: 'Skema Pembayaran wajib dipilih',
												},
											]}
										>
											<Select
												options={mapOptionsToSelect(paymentSchemeGiroOptions)}
												style={{ width: '100%' }}
												placeholder="Pilih Skema Pembayaran"
												allowClear
												disabled={viewOnly}
											/>
										</Form.Item>
									</Col>
								)}
							</Row>

							{/* Tenor Checkboxes Section */}
							<Form.Item
								noStyle
								shouldUpdate={() => true}
							>
								{({ getFieldValue: getVal }) => {
									const schemeId = getVal(
										fieldName('insurancePaymentSchemeGiroTypeId'),
									);
									const selectedScheme = paymentSchemeGiroOptions.find(
										(opt) => opt.id === schemeId,
									);
									const isBertahap = selectedScheme?.value
										?.toLowerCase()
										.includes('bertahap');

									// Only show if Scheme is Bertahap AND Scheme is visible (i.e. Lower Payment is PDC/Giro)
									if (!isBertahap || !isPdcGiro) {
										return null;
									}

									// Condition 3: Filter Checkboxes based on Years Covered by Main Payment
									// If Main Payment covers X years, Checkboxes start from X + 1
									const startYear = yearsCovered + 1;

									// If all years covered (but logic reached here?), imply nothing to select?
									// But validation above (isMainCash && covered >= max) might have caught it.
									// If Main is NOT Cash (e.g. Onloan) but covers years, we still filter.

									if (startYear > maxTenor) {
										return null;
									}

									const checkboxOptions = [];
									for (let i = startYear; i <= maxTenor; i++) {
										checkboxOptions.push({
											label: `Tahun Ke-${i}`,
											value: i,
										});
									}

									// If single option and it is the ONLY remaining year?
									// Logic says: if Tenor=1, check disabled.
									// Here, check if checkboxOptions.length === 1 AND startYear === 1?
									// Actually, if we have remaining years, let user manage them.
									// If only 1 year remains, maybe auto-select?
									// User prompt: "maka user bisa pilih 3 checkbox" -> implies manual.
									// Old logic: "disabled: maxTenor === 1 && i === 0" (Array index 0, which is Year 1)

									const isOnlyOneYearTotal = maxTenor === 1;
									// Adjust disabled logic: if we are listing Year 1 and it is the ONLY year of the whole tenor.
									const initialValues =
										isOnlyOneYearTotal && startYear === 1 ? [1] : [];

									return (
										<Row
											gutter={40}
											style={{ marginTop: 20 }}
										>
											<Col span={24}>
												<Form.Item
													labelCol={{ span: 6 }}
													name={fieldName('insuranceGiroTenor')}
													label="Pilih Tenor"
													initialValue={initialValues}
													rules={[
														{
															validator: async (_, value) => {
																if (isOnlyOneYearTotal && startYear === 1) {
																	return Promise.resolve();
																}
																if (!value || value.length === 0) {
																	return Promise.reject(
																		new Error('Minimal pilih 1 tenor'),
																	);
																}
																return Promise.resolve();
															},
														},
													]}
												>
													<Checkbox.Group
														options={checkboxOptions.map((opt) => ({
															...opt,
															disabled:
																isOnlyOneYearTotal &&
																opt.value === 1 &&
																viewOnly !== true,
														}))}
														disabled={viewOnly}
													/>
												</Form.Item>
											</Col>
										</Row>
									);
								}}
							</Form.Item>
						</>
					);
				}}
			</Form.Item>
		</>
	);
};

export default SimInsuranceMTFClause;
